import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { auth, signOut } from "../services/firebaseApi";
import {
  Menu,
  X,
  ChevronDown,
  Bell,
  LayoutDashboard,
  Cpu,
  BarChart3,
  Settings,
  User,
  AlertTriangle,
  Sparkles,
  Shield,
  Tag,
  HeartHandshake,
  LogOut,
  Crown,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import SafeImage from "./SafeImage";
import { USER_ROLES } from "../constants/appConstants";
import { getUserProfile } from "../services/profileService";

const mainLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/benefits", label: "Benefits" },
  { to: "/pricing", label: "Pricing" },
  { to: "/contact", label: "Contact" },
];

const appMega = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, desc: "Live device overview" },
  { to: "/devices", label: "Devices", icon: Cpu, desc: "Boards and readings" },
  { to: "/history", label: "History", icon: BarChart3, desc: "Trends & exports" },
  { to: "/notifications", label: "Notifications", icon: Bell, desc: "Activity feed" },
  { to: "/alerts", label: "Alerts", icon: AlertTriangle, desc: "Threshold rules" },
  { to: "/settings", label: "Settings", icon: Settings, desc: "Device & Wi-Fi" },
  { to: "/profile", label: "Profile", icon: User, desc: "Your account" },
  { to: "/future-updates", label: "Roadmap", icon: Sparkles, desc: "What's coming" },
];

const moreMega = [
  { to: "/privacy", label: "Privacy Policy", icon: Shield, desc: "How we handle data" },
  { to: "/pricing", label: "Pricing", icon: Tag, desc: "Coming soon" },
  { to: "/benefits", label: "Benefits", icon: HeartHandshake, desc: "What the app does" },
];

const Logo = ({ variant = "dark" }) => {
  return (
    <Link to="/" className="flex min-w-0 items-center gap-2 sm:gap-3 group shrink">
      <div className="relative h-8 w-[7.5rem] sm:h-10 sm:w-[9.5rem] shrink-0 overflow-hidden">
        <img src="/Images/Header%20Logo.svg" alt="Smart Farming" className="h-full w-full object-contain object-left p-0.5 sm:p-1" />
      </div>
    </Link>
  );
};

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMega, setOpenMega] = useState(null); // "app" | "more" | null
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const { language, busy, toggleLanguage } = useLanguage();
  const [profileName, setProfileName] = useState("");
  const [userRole, setUserRole] = useState(USER_ROLES.VIEWER);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMega(null);
    setUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    async function loadProfile() {
      if (!user?.uid) {
        setProfileName("");
        setUserRole(USER_ROLES.VIEWER);
        return;
      }
      try {
        const p = (await getUserProfile(user.uid)) || {};
        if (cancelled) return;
        setProfileName(p.displayName || p.fullName || "");
        setUserRole(p.role || USER_ROLES.VIEWER);
      } catch {
        if (!cancelled) {
          setProfileName("");
          setUserRole(USER_ROLES.VIEWER);
        }
      }
    }
    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  function formatName(name) {
    const cleaned = String(name || "").trim().replace(/\s+/g, " ");
    if (!cleaned) return "";
    const parts = cleaned.split(" ");
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
  }

  const displayName = formatName(profileName) || formatName(user?.displayName) || "Account";

  const initial = (displayName || "A").trim().charAt(0).toUpperCase();

  const doLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = "/login";
    } catch {
      // ignore
      window.location.href = "/login";
    }
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 py-3 sm:py-4">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex min-w-0 items-center justify-between gap-2 rounded-full px-3 sm:px-5 py-2 sm:py-2.5 bg-card border border-border shadow-soft">
          <Logo />

          <nav className="hidden lg:flex items-center gap-1 shrink-0">
            {mainLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `relative px-3.5 py-2 text-sm hover:text-accent transition-colors rounded-full ${isActive ? "text-accent font-medium" : "text-foreground/80"}`
                }
              >
                {l.label}
              </NavLink>
            ))}

            <button
              onMouseEnter={() => setOpenMega("app")}
              onMouseLeave={() => setOpenMega(null)}
              className="relative"
              type="button"
            >
              <span className="inline-flex items-center gap-1 px-3.5 py-2 text-sm text-foreground/80 hover:text-accent transition-colors rounded-full">
                App <ChevronDown className="h-3.5 w-3.5" />
              </span>
              {openMega === "app" && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 animate-[slide-down_0.3s_var(--ease-smooth)]">
                  <div className="bg-card border border-border rounded-3xl shadow-elevated p-3 w-[640px] grid grid-cols-2 gap-1">
                    {appMega.map((m) => (
                      <Link
                        key={m.to}
                        to={m.to}
                        className="flex gap-3 p-3 rounded-2xl hover:bg-accent/10 transition-colors group/i text-left"
                      >
                        <div className="h-10 w-10 shrink-0 rounded-xl bg-primary/10 flex items-center justify-center group-hover/i:bg-accent group-hover/i:text-accent-foreground transition-colors">
                          <m.icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-foreground">{m.label}</div>
                          <div className="text-xs text-muted-foreground">{m.desc}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </button>

            <button
              onMouseEnter={() => setOpenMega("more")}
              onMouseLeave={() => setOpenMega(null)}
              className="relative"
              type="button"
            >
              <span className="inline-flex items-center gap-1 px-3.5 py-2 text-sm text-foreground/80 hover:text-accent transition-colors rounded-full">
                More <ChevronDown className="h-3.5 w-3.5" />
              </span>
              {openMega === "more" && (
                <div className="absolute top-full right-0 pt-3 animate-[slide-down_0.3s_var(--ease-smooth)]">
                  <div className="bg-card border border-border rounded-3xl shadow-elevated p-3 w-80 grid gap-1">
                    {moreMega.map((m) => (
                      <Link
                        key={m.to}
                        to={m.to}
                        className="flex gap-3 p-3 rounded-2xl hover:bg-accent/10 transition-colors text-left"
                      >
                        <div className="h-9 w-9 shrink-0 rounded-xl bg-primary/10 flex items-center justify-center">
                          <m.icon className="h-4.5 w-4.5" />
                        </div>
                        <div>
                          <div className="text-sm font-medium">{m.label}</div>
                          <div className="text-xs text-muted-foreground">{m.desc}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </button>
          </nav>

          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <button
              type="button"
              data-no-translate
              onClick={toggleLanguage}
              aria-pressed={language === "hi"}
              aria-label={language === "hi" ? "Show English" : "हिंदी में पढ़ें"}
              title={language === "hi" ? "English" : "हिंदी"}
              className={`inline-flex h-10 w-10 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                language === "hi"
                  ? "bg-accent text-accent-foreground"
                  : "text-foreground/80 hover:bg-accent/10 hover:text-accent"
              } ${busy ? "opacity-70" : ""}`}
            >
              {language === "hi" ? "EN" : "हि"}
            </button>
            <Link to="/notifications" className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-accent/10 text-foreground/80 hover:text-accent transition-colors relative">
              <Bell className="h-4.5 w-4.5" />
              <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-accent animate-[pulse-glow_2.4s_ease-in-out_infinite]" />
            </Link>

            {!user ? (
              <Link
                to="/login"
                className="hidden sm:inline-flex items-center justify-center rounded-full bg-accent px-4 py-2 text-sm font-medium text-accent-foreground shadow-soft hover:shadow-glow transition-all"
              >
                Login
              </Link>
            ) : (
              <div className="hidden sm:block relative">
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((v) => !v)}
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-2 text-sm hover:shadow-soft transition-all"
                  aria-label="User menu"
                >
                  <span className="relative h-8 w-8 rounded-full overflow-hidden border border-border bg-secondary/50 flex items-center justify-center">
                    {user.photoURL ? (
                      <SafeImage src={user.photoURL} alt={displayName} className="h-full w-full object-cover" fallbackLabel={displayName} />
                    ) : (
                      <span className="font-display text-lg text-foreground">{initial}</span>
                    )}
                  </span>
                  <span className="max-w-[140px] truncate">{displayName}</span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full pt-3">
                    <div className="w-64 rounded-3xl border border-border bg-card shadow-elevated p-2">
                      <Link to="/profile" className="flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-accent/10 text-sm">
                        <User className="h-4 w-4" /> Profile
                      </Link>
                      <Link to="/settings" className="flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-accent/10 text-sm">
                        <Settings className="h-4 w-4" /> Settings
                      </Link>
                      {userRole === USER_ROLES.ADMIN && (
                        <Link to="/admin" className="flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-accent/10 text-sm">
                          <Shield className="h-4 w-4" /> Admin Dashboard
                        </Link>
                      )}
                      {userRole === USER_ROLES.SUPER_ADMIN && (
                        <Link to="/super-admin" className="flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-accent/10 text-sm">
                          <Crown className="h-4 w-4" /> Super Admin Dashboard
                        </Link>
                      )}
                      <div className="h-px bg-border my-1" />
                      <button
                        type="button"
                        onClick={doLogout}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-destructive/10 text-sm text-destructive"
                      >
                        <LogOut className="h-4 w-4" /> Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <button
              className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-full hover:bg-accent/10 transition-colors"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
              type="button"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden mt-2 bg-card border border-border rounded-3xl shadow-elevated animate-[slide-down_0.3s_var(--ease-smooth)] overflow-hidden max-h-[calc(100dvh-5.5rem)]">
            <div className="grid gap-1 p-3 sm:p-4 max-h-[calc(100dvh-5.5rem)] overflow-y-auto overscroll-contain">
              {mainLinks.map((l) => (
                <Link key={l.to} to={l.to} className="px-3 py-2.5 rounded-xl hover:bg-accent/10 text-sm">
                  {l.label}
                </Link>
              ))}
              <div className="h-px bg-border my-2" />
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-3 mb-1">App</div>
              {appMega.map((m) => (
                <Link key={m.to} to={m.to} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-accent/10 text-sm">
                  <m.icon className="h-4 w-4" /> {m.label}
                </Link>
              ))}
              <div className="h-px bg-border my-2" />
              {moreMega.map((m) => (
                <Link key={m.to} to={m.to} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-accent/10 text-sm">
                  <m.icon className="h-4 w-4" /> {m.label}
                </Link>
              ))}
              {!user ? (
                <Link to="/login" className="mt-2 inline-flex items-center justify-center rounded-full bg-accent px-4 py-2 text-sm font-medium text-accent-foreground">
                  Login
                </Link>
              ) : (
                <>
                  {userRole === USER_ROLES.ADMIN && (
                    <Link to="/admin" className="mt-2 inline-flex items-center justify-center rounded-full border border-border px-4 py-2 text-sm">
                      Admin Dashboard
                    </Link>
                  )}
                  {userRole === USER_ROLES.SUPER_ADMIN && (
                    <Link to="/super-admin" className="mt-2 inline-flex items-center justify-center rounded-full border border-border px-4 py-2 text-sm">
                      Super Admin Dashboard
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={doLogout}
                    className="mt-2 inline-flex items-center justify-center rounded-full border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm font-medium text-destructive"
                  >
                    Logout
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
